/**
 * BusinessCaja — "Salud de caja + Calendario de obligaciones" (CFO Autonomo, ladrillo 2).
 *
 * La empresa ve sus obligaciones (IVA/nomina/prima/cesantias) con las FECHAS ya
 * sugeridas (ley exactas; DIAN estimada, a confirmar), llena los MONTOS y su saldo,
 * y el sistema le dice si le alcanza: semaforo verde/amarillo/rojo + "en N dias te
 * faltan $X". v1 SOLO ESTIMA: no mueve dinero. Backend: investments-api /api/business-caja.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCaja, saveCaja, saludCaja, sugerirFechasCaja } from '../../lib/cerebro';
import BusinessCajaTotal from './BusinessCajaTotal';

const fmtMonto = (n, moneda) =>
  moneda === 'USD'
    ? `$${Number(n || 0).toLocaleString('en-US')} USD`
    : `$${Math.round(Number(n || 0)).toLocaleString('es-CO')} COP`;

const fmtFecha = (d) =>
  d ? new Date(d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Sin fecha';

const toInputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const SEM = {
  verde: { tone: 'verde', label: 'Vas bien' },
  amarillo: { tone: 'amarillo', label: 'Cuidado' },
  rojo: { tone: 'rojo', label: 'Ojo' },
};

const FUENTE = {
  ley: { txt: 'por ley', tone: 'good' },
  dian_oficial: { txt: 'DIAN · tu NIT', tone: 'good' },
  dian_aprox: { txt: 'aprox DIAN · confírmala', tone: 'warn' },
  manual: { txt: '', tone: '' },
};

export default function BusinessCaja() {
  const [cfg, setCfg] = useState(null);
  const [obligaciones, setObligaciones] = useState([]);
  const [iva, setIva] = useState('cuatrimestral');
  const [saldo, setSaldo] = useState('');
  const [salud, setSalud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [piloto, setPiloto] = useState(false);
  const [sinEmpresa, setSinEmpresa] = useState(false);

  // Sin monto = el backend usa la CAJA TOTAL REAL (FlowexPay + bancos).
  // Con monto = simulacion ("¿y si tuviera X?").
  const calcular = useCallback(async (s) => {
    const val = Number(s);
    const usarReal = s === '' || s === null || s === undefined || !Number.isFinite(val) || val < 0;
    try {
      const r = await saludCaja(usarReal ? undefined : val);
      if (r?.ok) setSalud(r); else setSalud(null);
    } catch { /* silencioso: el semaforo simplemente no aparece */ }
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getCaja();
      if (d?.sinEmpresa) { setSinEmpresa(true); return; }
      setCfg(d);
      setObligaciones(d.obligaciones || []);
      setIva(d.ivaPeriodicidad || 'cuatrimestral');
      // Siempre arranca con la CAJA REAL (FlowexPay + bancos). El saldo manual queda
      // solo para simular; ya no es la fuente de verdad.
      calcular('');
      setError('');
    } catch (err) {
      if (err?.response?.status === 403) setPiloto(true);
      else setError('No pudimos cargar tu caja. Reintenta.');
    } finally {
      setLoading(false);
    }
  }, [calcular]);

  useEffect(() => { cargar(); }, [cargar]);

  const setRow = (i, patch) => setObligaciones((prev) => prev.map((o, k) => (k === i ? { ...o, ...patch } : o)));

  const guardar = async () => {
    setSaving(true); setOkMsg(''); setError('');
    try {
      const r = await saveCaja(obligaciones, saldo === '' ? null : Number(saldo), iva);
      if (r?.obligaciones) { setObligaciones(r.obligaciones); setOkMsg('Listo, guardamos tu calendario.'); }
      await calcular(saldo);
      setTimeout(() => setOkMsg(''), 4000);
    } catch {
      setError('No pudimos guardar. Reintenta.');
    } finally { setSaving(false); }
  };

  const recalcularFechas = async (nuevaIva) => {
    try {
      const r = await sugerirFechasCaja(nuevaIva || iva);
      if (r?.obligaciones) { setObligaciones(r.obligaciones); setIva(r.ivaPeriodicidad || iva); }
    } catch { /* no-op */ }
  };

  const addRow = () =>
    setObligaciones((prev) => [...prev, { clave: `custom-${Date.now()}`, nombre: '', monto: 0, fecha: null, periodicidad: 'unica', fuente: 'manual', activo: true }]);

  if (loading) return <div className="caja-ola"><div className="caja-ola__loading">Cargando tu caja…</div></div>;

  if (piloto || sinEmpresa)
    return (
      <div className="caja-ola">
        <div className="caja-ola__panel">
          <h1 className="caja-ola__title sm">{piloto ? 'FlowexPay Business está en piloto privado' : 'Aún no tienes una empresa vinculada'}</h1>
          <p className="caja-ola__sub">{piloto ? 'Escríbenos para activar tu empresa y usar la salud de caja.' : 'Crea o vincula tu empresa para ver tu salud de caja.'}</p>
        </div>
      </div>
    );

  const sem = salud ? SEM[salud.semaforo] || SEM.verde : null;

  return (
    <div className="caja-ola">
      <header className="caja-ola__head">
        <div>
          <p className="caja-ola__eyebrow">Tesorería</p>
          <h1 className="caja-ola__title">Salud de caja</h1>
          <p className="caja-ola__sub">Nunca te quedes corto con la DIAN ni con la nómina.</p>
        </div>
        <Link to="/business" className="caja-ola__btn caja-ola__btn--ghost">Pregúntale al Oráculo →</Link>
      </header>

      {/* Caja Total: FlowexPay + los bancos (extractos leidos con IA, sin claves) */}
      <BusinessCajaTotal />

      <section className="caja-ola__panel">
        {salud?.fuenteSaldo === 'caja_total' ? (
          <>
            <div className="caja-ola__metric-lbl good">Tu caja de hoy · real</div>
            <div className="caja-ola__big">
              ${Number(salud.saldo || 0).toLocaleString('es-CO')} <span>{salud.moneda || 'COP'}</span>
            </div>
            <p className="caja-ola__hint">
              ${Number(salud.detalleSaldo?.flowexpayCop || 0).toLocaleString('es-CO')} en FlowexPay + ${Number(salud.detalleSaldo?.bancosCop || 0).toLocaleString('es-CO')} en bancos
              {salud.detalleSaldo?.hayExtractosViejos ? ' · hay un extracto con más de 15 días' : ''}
            </p>
          </>
        ) : (
          <div className="caja-ola__metric-lbl">Sube tu extracto arriba para calcular tu salud con tu caja real</div>
        )}
        <details className="caja-ola__details">
          <summary>Simular con otro saldo</summary>
          <div className="caja-ola__sim-row">
            <div className="caja-ola__input-wrap">
              <span>$</span>
              <input type="number" inputMode="numeric" value={saldo} onChange={(e) => setSaldo(e.target.value)} placeholder="5.000.000" className="caja-ola__input bare" />
              <span className="caja-ola__hint">{cfg?.moneda || 'COP'}</span>
            </div>
            <button type="button" onClick={() => calcular(saldo)} className="caja-ola__btn caja-ola__btn--primary">Simular</button>
            {salud?.fuenteSaldo === 'manual' && (
              <button type="button" onClick={() => { setSaldo(''); calcular(''); }} className="caja-ola__text-btn">Volver a mi caja real</button>
            )}
          </div>
        </details>
      </section>

      {sem && (
        <section className={`caja-ola__semaforo caja-ola__semaforo--${sem.tone}`}>
          <div className="caja-ola__lights" aria-hidden>
            {['rojo', 'amarillo', 'verde'].map((c) => (
              <span key={c} className={`caja-ola__light${salud.semaforo === c ? ` is-${c}` : ''}`} />
            ))}
          </div>
          <div>
            <div className="caja-ola__semaforo-label">Semáforo · {sem.label}</div>
            <div className="caja-ola__semaforo-text">{salud.resumenTexto}</div>
            {salud.nota && <div className="caja-ola__semaforo-note">{salud.nota}</div>}
          </div>
        </section>
      )}

      {salud?.detalle?.length > 0 && (
        <section className="caja-ola__panel">
          <div className="caja-ola__metric-lbl mb">Tu calendario de pagos</div>
          <div className="caja-ola__pay-row">
            <div className="caja-ola__pay-when"><b>Hoy</b></div>
            <div className="caja-ola__pay-icon is-bal">$</div>
            <div className="caja-ola__pay-name">Saldo en caja</div>
            <div className="caja-ola__pay-amt">{fmtMonto(salud.saldo, salud.moneda)}</div>
          </div>
          {salud.detalle.map((o, i) => (
            <div key={i} className="caja-ola__pay-row">
              <div className="caja-ola__pay-when"><b>{fmtFecha(o.fecha)}</b><span>en {o.dias} días</span></div>
              <div className={`caja-ola__pay-icon${o.cubierto ? ' is-ok' : ' is-bad'}`}>{o.cubierto ? '✓' : '!'}</div>
              <div className="caja-ola__pay-body">
                <div className="caja-ola__pay-name">{o.nombre}</div>
                <div className="caja-ola__hint">{o.cubierto ? 'Alcanza' : 'No alcanza'}</div>
              </div>
              <div className="caja-ola__pay-right">
                <div className={`caja-ola__pay-amt${o.cubierto ? '' : ' is-bad'}`}>− {fmtMonto(o.monto, salud.moneda)}</div>
                <div className={`caja-ola__hint${o.saldoDespues < 0 ? ' is-bad' : ''}`}>te queda {fmtMonto(o.saldoDespues, salud.moneda)}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="caja-ola__panel">
        <div className="caja-ola__panel-top wrap">
          <div className="caja-ola__metric-lbl">Tus obligaciones</div>
          <div className="caja-ola__iva-row">
            <span className="caja-ola__hint">IVA:</span>
            <select value={iva} onChange={(e) => { setIva(e.target.value); recalcularFechas(e.target.value); }} className="caja-ola__select">
              <option value="cuatrimestral">Cuatrimestral</option>
              <option value="bimestral">Bimestral</option>
            </select>
            <button type="button" onClick={() => recalcularFechas()} className="caja-ola__btn caja-ola__btn--ghost sm">Recalcular fechas</button>
          </div>
        </div>
        {obligaciones.map((o, i) => (
          <div key={o.clave || i} className="caja-ola__obl">
            <div>
              <input value={o.nombre} onChange={(e) => setRow(i, { nombre: e.target.value })} placeholder="Obligación" className="caja-ola__input bare bold" />
              {FUENTE[o.fuente]?.txt && <span className={`caja-ola__pill caja-ola__pill--${FUENTE[o.fuente].tone}`}>{FUENTE[o.fuente].txt}</span>}
            </div>
            <div className="caja-ola__input-wrap">
              <span>$</span>
              <input type="number" inputMode="numeric" value={o.monto || ''} onChange={(e) => setRow(i, { monto: Number(e.target.value) })} placeholder="0" className="caja-ola__input bare" />
            </div>
            <input type="date" value={toInputDate(o.fecha)} onChange={(e) => setRow(i, { fecha: e.target.value, fuente: 'manual' })} className="caja-ola__input" />
          </div>
        ))}
        <button type="button" onClick={addRow} className="caja-ola__text-btn mt">+ Agregar obligación</button>
        <div className="caja-ola__footer-row">
          <div className="caja-ola__hint italic">Esto es una estimación con tu saldo y tus obligaciones de hoy. No mueve dinero.</div>
          <div className="caja-ola__cta-row">
            {okMsg && <span className="caja-ola__ok">{okMsg}</span>}
            {error && <span className="caja-ola__err">{error}</span>}
            <button type="button" onClick={guardar} disabled={saving} className="caja-ola__btn caja-ola__btn--primary">{saving ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      </section>
    </div>
  );
}
